import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FluentDialog2 } from "../../../../components/FluentUI/Fluent-Dialog2";
import FluentUiIcon from "../../../../assets/fluent-ui-icon";
import { FluentButton } from "../../../../components/FluentUI/Fluent-Button";
import { FluentDatePicker } from '../../../../components/FluentUI/Fluent-DatePicker';
import { makeStyles, shorthands } from '@fluentui/react-components';
import DownloadProgressBar from '../../dynamic-fields/download-data-modal/download-progress-bar';
import  '../../dynamic-fields/download-data-modal/download-data-modal.scss';
import { setTimeToZero, addDays } from '../../../../util/time-helpers';
import TriyoDownloadImage from '../../../../assets/Illustrations/TriyoDownload.svg';
import { FluentDropdown } from '../../../../components/FluentUI/Fluent-Dropdown.jsx';

const useStyles = makeStyles({
  root: {
    display: "flex",
    padding: "5px",
    ...shorthands.border("1px", "solid", "#EEEEEE"),
    resize: "vertical !important",
    fontSize: "12px !important",
    color: "#4E4E4E",
    height: "36px",
    marginTop: "6px",
  },
});

const DownloadHistoricalDataModal = ({ open, OnCancel, OnDownloadData }) => {
const [startDate, setStartDate] = useState(() => {
  const fourWeeksAgo = addDays(new Date(), -28);
  // Snap to closest Monday (Sun=0 ... Sat=6)
  const day = fourWeeksAgo.getDay();
  const offsetToClosestMonday = ((1 - day + 3 + 7) % 7) - 3;
  return setTimeToZero(addDays(fourWeeksAgo, offsetToClosestMonday));
});
  const today = setTimeToZero(new Date());
  const [endDate, setEndDate] = useState(today);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState(null);
  const [progressValue, setProgressValue] = useState(0);
  const [selectedData, setSelectedData] = useState("<GIB/PU&I> Weekly Hour Breakdown by Users, Deals and Senior Mgmt.");

  const fieldStyle = useStyles();
  const dataOptions = [
    "<GIB/PU&I> Weekly Hour Breakdown by Users, Deals and Senior Mgmt.",
  ];

  useEffect(() => {
    let intervalId;

    if (isDownloading && downloadStatus === 'pending') {
      intervalId = setInterval(() => {
        setProgressValue(prev => {
          const nextValue = prev + 0.02;
          return nextValue >= 0.8 ? 0.8 : nextValue;
        });
      }, 100);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isDownloading, downloadStatus]);

  const handleStartDateChange = (newStart,selectedDateRangeArray) => {
    if (!newStart) return;

    if (newStart >= endDate) {
      setEndDate(addDays(newStart, 1));
    }
    setStartDate(newStart);
  };

  const handleEndDateChange = (newEnd) => {
    if (!newEnd) return;

    if (newEnd <= startDate) {
      setStartDate(addDays(newEnd, -1));
    }
    setEndDate(newEnd);
  };

  const handleDownloadData = async () => {
    setIsDownloading(true);
    setDownloadStatus('pending');
    setProgressValue(0);

    try {
      const response = await OnDownloadData(startDate, endDate, selectedData);
      setProgressValue(1);

      if (response && response.status === 200) {
        setDownloadStatus('success');
      } else {
        setDownloadStatus('error');
      }
    } catch (err) {
      setProgressValue(1);
      setDownloadStatus('error');
    }
  };

  return (
    <FluentDialog2 open={open}>
      <div className="data-modal-dialog-title">
        <p>Download Historical Data Report</p>
        <p onClick={OnCancel}>
          <FluentUiIcon name={"DismissRegular"} color={"#212121"} size={20} />
        </p>
      </div>

      <div className="data-modal-dialog-content">
        {!isDownloading && (
          <>
            <div className="mb-3">
              <FluentDropdown
                label="Select a Report"
                placeholder="Choose data to download..."
                options={dataOptions}
                selectedOptions={selectedData}
                onChange={(e) => {
                  setSelectedData(e.target.textContent)
                }}
                defaultValue={dataOptions[0]}
                disabled={true}
              />
            </div>
            <div className="data-modal-dialog-content-dates">
              <FluentDatePicker
                label="Start Week"
                placeholder="Select a date..."
                size="small"
                maxDate={addDays(endDate, -1)}
                value={startDate}
                required
                onChange={handleStartDateChange}
                componentStyle={fieldStyle.root}
                isWeekSelectionEnabled={true}
                firstDayOfWeek={1}
                dayOftheWeekSelection={0}
              />
              <FluentDatePicker
                label="End Week"
                placeholder="Select a date..."
                size="small"
                minDate={addDays(startDate, 1)}
                maxDate={today}
                value={endDate}
                required
                onChange={handleEndDateChange}
                componentStyle={fieldStyle.root}
                isWeekSelectionEnabled={true}
                firstDayOfWeek={1}
                dayOftheWeekSelection={6}
              />
            </div>
          </>
        )}

        {isDownloading && (
          <div className="data-modal-dialog-placeholder">
            <img
              src={TriyoDownloadImage}
              alt="Download Data"
              className="download-data-image"
            />
            <DownloadProgressBar
              downloadStatus={downloadStatus}
              progressValue={progressValue}
            />
          </div>
        )}
      </div>

      {/* Left Side Footer: empty at the moment*/}
      <div>
      </div>

      {/* Right Side Footer */}
      {(!isDownloading || downloadStatus === 'error') && (
        <div className="data-modal-dialog-footer col">
          <FluentButton
            onClick={OnCancel}
            text="Cancel"
            backgroundColor="#0F6CBD"
          />
          <FluentButton
            onClick={handleDownloadData}
            appearance="primary"
            text={downloadStatus === 'error' ? "Try again" : "Download Report"}
            backgroundColor="#0F6CBD"
          />
        </div>
      )}
    </FluentDialog2>
  );
};

DownloadHistoricalDataModal.propTypes = {
  open: PropTypes.bool.isRequired,
  OnCancel: PropTypes.func.isRequired,
  OnDownloadData: PropTypes.func.isRequired,
};

export default DownloadHistoricalDataModal;
